import styled from 'styled-components';
import {
  ATTENTION_BLUE,
  SIDEBAR_BG,
  SIDEBAR_TEXT,
  SIDEBAR_TEXT_ACTIVE,
  SIDEBAR_ITEM_BG,
  SIDEBAR_ITEM_ACTIVE_BG,
  SIDEBAR_ITEM_HOVER_BG,
  SIDEBAR_BORDER,
  SIDEBAR_SEARCH_BG,
  ERROR_RED,
  TEXT_SECONDARY,
} from '../../../constants/colors';
import { FONT_FAMILY, FONT_SIZE } from '../../../constants/fonts';
import { MEDIA } from '../../../constants/breakpoints';

export const AgentSidebar = styled.aside<{ $collapsed: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${SIDEBAR_BG};
  overflow: hidden;
  transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const AgentSidebar__Header = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$collapsed ? '8px' : '12px')};
  padding: ${(props) => (props.$collapsed ? '20px 0 16px' : '20px 20px 16px')};
  border-bottom: 1px solid ${SIDEBAR_BORDER};
  justify-content: ${(props) => (props.$collapsed ? 'center' : 'flex-start')};
  flex-direction: ${(props) => (props.$collapsed ? 'column' : 'row')};
`;

export const AgentSidebar__Logo = styled.img`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
`;

export const AgentSidebar__Title = styled.h2<{ $collapsed: boolean }>`
  margin: 0;
  color: ${SIDEBAR_TEXT_ACTIVE};
  font-family: ${FONT_FAMILY.SEMI_BOLD};
  font-size: ${FONT_SIZE.TITLE};
  display: ${(props) => (props.$collapsed ? 'none' : 'block')};
  white-space: nowrap;
`;

export const AgentSidebar__HomeTile = styled.div<{ $active: boolean; $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$collapsed ? '0' : '12px')};
  padding: ${(props) => (props.$collapsed ? '10px' : '12px')};
  margin: ${(props) => (props.$collapsed ? '12px 8px 0' : '12px 12px 0')};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.$active ? SIDEBAR_ITEM_ACTIVE_BG : SIDEBAR_ITEM_BG)};
  border: 1px solid ${(props) => (props.$active ? ATTENTION_BLUE : SIDEBAR_BORDER)};
  justify-content: ${(props) => (props.$collapsed ? 'center' : 'flex-start')};

  &:hover {
    background: ${(props) => (props.$active ? SIDEBAR_ITEM_ACTIVE_BG : SIDEBAR_ITEM_HOVER_BG)};
  }
`;

export const AgentSidebar__HomeTileIcon = styled.div<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(props) => (props.$active ? ATTENTION_BLUE : 'rgba(255, 255, 255, 0.15)')};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  transition: background 0.2s ease;
`;

export const AgentSidebar__HomeTileName = styled.span<{ $active: boolean; $collapsed: boolean }>`
  flex: 1;
  color: ${(props) => (props.$active ? SIDEBAR_TEXT_ACTIVE : SIDEBAR_TEXT)};
  font-family: ${FONT_FAMILY.SEMI_BOLD};
  font-size: ${FONT_SIZE.MEDIUM};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: ${(props) => (props.$collapsed ? 'none' : 'block')};
`;

export const AgentSidebar__HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: auto;
  flex-shrink: 0;
`;

export const AgentSidebar__CollapseBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${SIDEBAR_TEXT};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    color: ${SIDEBAR_TEXT_ACTIVE};
    background: rgba(255, 255, 255, 0.1);
  }
`;

export const AgentSidebar__Search = styled.input`
  width: 100%;
  padding: 10px 12px;
  margin: 12px 0 4px;
  background: ${SIDEBAR_SEARCH_BG};
  border: 1px solid ${SIDEBAR_BORDER};
  border-radius: 8px;
  color: ${SIDEBAR_TEXT_ACTIVE};
  font-family: ${FONT_FAMILY.REGULAR};
  font-size: ${FONT_SIZE.SMALL};
  outline: none;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: ${SIDEBAR_TEXT};
    opacity: 0.7;
  }

  &:focus {
    border-color: ${ATTENTION_BLUE};
    background: rgba(255, 255, 255, 0.12);
  }
`;

export const AgentSidebar__SearchWrap = styled.div<{ $collapsed: boolean }>`
  padding: 0 12px;
  display: ${(props) => (props.$collapsed ? 'none' : 'block')};
`;

export const AgentSidebar__List = styled.div<{ $collapsed: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: ${(props) => (props.$collapsed ? '12px 8px' : '12px 12px')};

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${ATTENTION_BLUE}80 transparent;

  /* WebKit (Chrome, Edge, Safari) */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${ATTENTION_BLUE}66;
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: padding-box;
    transition: background 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${ATTENTION_BLUE}cc;
    background-clip: padding-box;
  }

  /* Hide the up/down arrow buttons */
  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }
`;

export const AgentSidebar__Item = styled.div<{ $active: boolean; $collapsed: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$collapsed ? '0' : '12px')};
  padding: ${(props) => (props.$collapsed ? '10px' : '12px')};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) => (props.$active ? SIDEBAR_ITEM_ACTIVE_BG : SIDEBAR_ITEM_BG)};
  border: 1px solid ${(props) => (props.$active ? ATTENTION_BLUE : SIDEBAR_BORDER)};
  margin-bottom: 6px;
  justify-content: ${(props) => (props.$collapsed ? 'center' : 'flex-start')};

  &:hover {
    background: ${(props) => (props.$active ? SIDEBAR_ITEM_ACTIVE_BG : SIDEBAR_ITEM_HOVER_BG)};
  }

  /* Show new-chat button on hover or when active */
  &:hover button[data-role='new-chat'],
  ${(props) => props.$active && `button[data-role='new-chat']`} {
    opacity: 1;
  }
`;

export const AgentSidebar__ItemIcon = styled.div<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${(props) => (props.$active ? ATTENTION_BLUE : 'rgba(255, 255, 255, 0.15)')};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  transition: background 0.2s ease;
`;

export const AgentSidebar__ItemName = styled.span<{ $active: boolean; $collapsed: boolean }>`
  flex: 1;
  color: ${(props) => (props.$active ? SIDEBAR_TEXT_ACTIVE : SIDEBAR_TEXT)};
  font-family: ${FONT_FAMILY.SEMI_BOLD};
  font-size: ${FONT_SIZE.MEDIUM};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: ${(props) => (props.$collapsed ? 'none' : 'block')};

  ${MEDIA.MOBILE} {
    font-size: ${FONT_SIZE.SMALL};
  }
`;

export const AgentSidebar__ItemActions = styled.div<{ $collapsed: boolean }>`
  display: ${(props) => (props.$collapsed ? 'none' : 'flex')};
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

export const AgentSidebar__ItemInfoBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${SIDEBAR_TEXT};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: ${SIDEBAR_TEXT_ACTIVE};
  }
`;

export const AgentSidebar__NewChatBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${SIDEBAR_TEXT};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  opacity: 0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: ${ATTENTION_BLUE};
  }
`;

export const AgentSidebar__UserSection = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$collapsed ? '0' : '12px')};
  padding: ${(props) => (props.$collapsed ? '16px 0' : '16px 20px')};
  border-top: 1px solid ${SIDEBAR_BORDER};
  justify-content: ${(props) => (props.$collapsed ? 'center' : 'flex-start')};
`;

export const AgentSidebar__UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${ATTENTION_BLUE};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${FONT_FAMILY.SEMI_BOLD};
  font-size: ${FONT_SIZE.SMALL};
  flex-shrink: 0;
`;

export const AgentSidebar__UserInfo = styled.div<{ $collapsed: boolean }>`
  flex: 1;
  min-width: 0;
  display: ${(props) => (props.$collapsed ? 'none' : 'block')};
`;

export const AgentSidebar__UserName = styled.div`
  color: ${SIDEBAR_TEXT_ACTIVE};
  font-family: ${FONT_FAMILY.SEMI_BOLD};
  font-size: ${FONT_SIZE.SMALL};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AgentSidebar__UserEmail = styled.div`
  color: ${SIDEBAR_TEXT};
  font-family: ${FONT_FAMILY.REGULAR};
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${MEDIA.MOBILE} {
    display: none;
  }
`;

export const AgentSidebar__LogoutBtn = styled.button<{ $collapsed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: ${SIDEBAR_TEXT};
  cursor: pointer;
  display: ${(props) => (props.$collapsed ? 'none' : 'flex')};
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(244, 67, 54, 0.15);
    color: #f44336;
  }
`;

/* --- State indicators (loading / error / empty) --- */

export const AgentSidebar__StateWrap = styled.div<{ $color?: 'error' | 'muted' }>`
  text-align: center;
  padding: 24px 12px;
  color: ${(props) => (props.$color === 'error' ? ERROR_RED : TEXT_SECONDARY)};
  font-family: ${FONT_FAMILY.REGULAR};
  font-size: ${FONT_SIZE.SMALL};
`;

export const AgentSidebar__StateIcon = styled.div`
  font-size: 24px;
  display: block;
  margin-bottom: 8px;
`;

export const AgentSidebar__RetryBtn = styled.button`
  background: none;
  border: 1px solid ${ERROR_RED};
  color: ${ERROR_RED};
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;
  font-family: ${FONT_FAMILY.REGULAR};
  font-size: ${FONT_SIZE.SMALL};
  transition: all 0.2s ease;

  &:hover {
    background: rgba(220, 38, 38, 0.1);
  }
`;

export const AgentSidebar__ItemImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 4px;
  object-fit: cover;
`;
